import { ContentfulClientApi } from "contentful";
import { client as Apollo } from "lib/Apollo";
import { ApolloClient } from "apollo-client";
import { NormalizedCacheObject } from "apollo-cache-inmemory";
import hashify from "object-hash";
import gql from "graphql-tag";

import { ContentfulContentType } from "types";

export enum ClientId {
  CONTENTFUL = "contentful",
  APOLLO = "apollo"
}

export interface ShopifyVariables {
  [variable: string]: any;
}

type ApiClientType = {
  cache: {
    getEntries: {
      [hash: string]: unknown;
    };
    query: {
      [hash: string]: unknown;
    };
  };
  setRef: (
    clientId: ClientId,
    client: ContentfulClientApi | ApolloClient<NormalizedCacheObject>
  ) => void;
  getEntries: (query: Query) => Promise<unknown>;
  query: (
    query: string,
    variables?: ShopifyVariables,
    disableCache?: boolean
  ) => Promise<unknown>;
  mutate: (query: string, variables: ShopifyVariables) => Promise<unknown>;
  clients: {
    contentful: ContentfulClientApi | null;
    apollo: ApolloClient<NormalizedCacheObject> | null;
  };
};

type Query = {
  content_type: ContentfulContentType;
  include: number;
};

const ApiClient: ApiClientType = {
  cache: {
    getEntries: {},
    query: {}
  },
  clients: {
    contentful: null,
    apollo: null
  },
  setRef(
    clientId: ClientId,
    client: ContentfulClientApi | ApolloClient<NormalizedCacheObject>
  ) {
    if (clientId === ClientId.CONTENTFUL) {
      this.clients[clientId] = client as ContentfulClientApi;
    }

    if (clientId === ClientId.APOLLO) {
      this.clients[clientId] = client as ApolloClient<NormalizedCacheObject>;
    }
  },
  getEntries(query: Query) {
    const hashified = hashify(query);

    if (!!this.cache.getEntries[hashified]) {
      return new Promise(resolve => resolve(this.cache.getEntries[hashified]));
    }

    if (this.clients.contentful) {
      return this.clients.contentful.getEntries(query).then((val: unknown) => {
        this.cache.getEntries[hashified] = val;

        return val;
      });
    }

    throw new Error("no contentful client found in ApiClient");
  },
  query(
    query: string,
    variables?: ShopifyVariables,
    disableCache: boolean = false
  ) {
    const hashified = hashify(query);

    if (!disableCache && !!this.cache.query[hashified]) {
      return new Promise(resolve => resolve(this.cache.query[hashified]));
    }

    if (!!variables) {
      return Apollo.query({
        query: gql`
          ${query}
        `,
        variables
      }).then((val: unknown) => {
        this.cache.query[hashified] = val;

        return val;
      });
    }

    return Apollo.query({
      query: gql`
        ${query}
      `
    }).then((val: unknown) => {
      this.cache.query[hashified] = val;

      return val;
    });
  },
  mutate(query: string, variables: ShopifyVariables) {
    return Apollo.mutate({
      mutation: gql`
        ${query}
      `,
      variables
    }).then((val: unknown) => {
      return val;
    });
  }
};

export default ApiClient;
